/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    ICheckoutViewProps,
    IEmailDelivery,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { getCartState } from '@msdyn365-commerce/global-state';
import { ICheckoutResources as ICheckoutResourcesExt } from '../definition-extensions/checkout.ext.props.autogenerated';
import { find } from 'lodash';

import * as React from 'react';
export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => {
    return (
        <Node {...LineItem}>
            {item}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
            {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
        </Node>
    );
};

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
            })
            : null}
    </Node>
);

const _renderCustomTaxLine = (taxAmount: number | null, props: ICheckoutViewPropsExt): JSX.Element | null => {
    const { taxLabel, toBeCalculatedText } = props.resources;

    return (
        <div style={{ marginBottom: '1rem' }}>
            <p className="msc-order-summary__line-tax">
                <span className="msc-order-summary__label">{taxLabel}</span>

                <span className="msc-order-summary__value">{
                    taxAmount && taxAmount > 0 ? `${taxAmount}` : toBeCalculatedText
                }</span>
            </p>
        </div>
    )
}

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines, ...other }) => {
    // @ts-ignore
    const locale = other.locale;
    // @ts-ignore
    const props = other.props;
    // @ts-ignore
    const taxAmount = other.taxAmount;

    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {locale.toLowerCase() == 'en-gb' ? _renderCustomTaxLine(taxAmount, props) : lines.tax}
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};
interface ICheckoutViewPropsExt extends ICheckoutViewProps {
    resources: ICheckoutResourcesExt;
}
// tslint:disable-next-line:max-func-body-length
const CheckoutView: React.FC<ICheckoutViewPropsExt> = props => {
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton
    } = props;

    const locale = props.checkoutProps.moduleProps.context.request.locale;
    const taxAmount = props?.data?.checkout?.result?.checkoutCart?.cart?.TaxAmount;

    const extraOrdersSummaryEu = {
        taxAmount: taxAmount,
        locale: locale,
        props: props
    };
    const [greeting, setGreeting] = React.useState({
        status: false,
        greetingMessageCAP: '',
        greetingSignCAP: ''
    });


    const getCartProps = async () => {
        const activeCartState = await getCartState(props.context?.actionContext);
        const checkoutCartState = props?.data?.checkout?.result?.checkoutCart;
        // prettier-ignore
        // @ts-ignore
        const extPropsToRemove = checkoutCartState?.cart?.ExtensionProperties?.filter((fieldVal: { Key: string }) => fieldVal.Key === 'ExtPropsRemove_CAP')[0]?.Value?.BooleanValue;
        // prettier-ignore
        if (extPropsToRemove === true) {
            const extKeysToRemove = ['ActiveCartId_CAP', 'CheckoutCartId_CAP', 'GreetingMessage_CAP', 'GreetingSign_CAP', 'billing', 'Type_CAP', 'Country_CAP', 'City_CAP', 'ZipCode_CAP', 'OrgName_CAP',
                'VatNum_CAP', 'FiscalCode_CAP', 'PEC_CAP', 'SDI_CAP', 'InvoiceCustAccount_CAP', 'Street_CAP', 'StreetNumber_CAP', 'FirstName_CAP', 'LastName_CAP'];

            const removeExtProps = async () => {
                const extProps = checkoutCartState?.cart?.ExtensionProperties;
                const extPropsToEmpty = [];
                if (extProps) {
                    for (const propToRemove of extKeysToRemove) {
                        if (find(extProps, obj => obj.Key === propToRemove)) {
                            extPropsToEmpty.push({
                                Key: propToRemove,
                                Value: {}
                            });
                        }
                    }
                    extPropsToEmpty.push({
                        Key: 'ExtPropsRemove_CAP',
                        Value: {
                            BooleanValue: false
                        }
                    });
                }
                await activeCartState?.updateExtensionProperties({ newExtensionProperties: extPropsToEmpty });
                await checkoutCartState?.updateExtensionProperties({ newExtensionProperties: extPropsToEmpty });
            };
            void removeExtProps();
        } else {
            // prettier-ignore
            // @ts-ignore
            const greetingMessageCAP = checkoutCartState?.cart?.ExtensionProperties?.filter((greetingVal: { Key: string }) => greetingVal.Key === 'GreetingMessage_CAP')[0]?.Value?.StringValue;
            // prettier-ignore
            // @ts-ignore
            const greetingSignCAP = checkoutCartState?.cart?.ExtensionProperties?.filter((greetingVal: { Key: string }) => greetingVal.Key === 'GreetingSign_CAP')[0]?.Value?.StringValue;
            setGreeting({
                status:
                    (greetingMessageCAP !== null && greetingMessageCAP !== undefined && greetingMessageCAP !== '') ||
                    (greetingSignCAP !== null && greetingSignCAP !== undefined && greetingSignCAP !== ''),
                greetingMessageCAP: greetingMessageCAP !== undefined ? greetingMessageCAP : '',
                greetingSignCAP: greetingSignCAP !== undefined ? greetingSignCAP : ''
            });
        }
    };

    React.useEffect(() => {
        if (canShow) {
            void getCartProps();
        }
    }, [canShow]);

    // function sleep(ms: number) {
    //     return new Promise(resolve => setTimeout(resolve, ms));
    // }

    // React.useEffect(() => {
        /*
        V item_id: “114066”, //product_id, SKU DL_CI
        V item_name: “Crema spalmabile Suprema Nocciola 250g”, DL_CI
        V coupon: “Coupon prodotto”, //coupon a livello di prodotto CHECKOUT
        V discount: 2.85, //Qualunque sconto associato CHECKOUT
        V item_discount_rate: "30%", CHECKOUT
        V index: 0, // la posizione del prodotto nel listato, inizia da 0 DL_CI
        item_category: “Cioccolato“ / “Pick&Mix” / “Idee regalo”, //valori delle breadcrumbs DL_CI
        item_list_name:  "Cioccolato" / "Pick&Mix" / "Best seller" / "Search results", DL_CI
        V price: 9.50, DL_CI
        V quantity: 1 DL_CI
        */

        // function getCheckoutCartItemsGA() {
        //     // getDLCart().then(dl_cart => {
        //     const checkout_cart_items = document.getElementsByClassName('msc-cart-line__content');
        //     // if (dl_cart.length !== checkout_cart_items.length) {
        //     //     return;
        //     // }
        //     let cart_items: {}[] = [];
        //     Array.from(checkout_cart_items).forEach((item, index) => {
        //         cart_items.push({
        //             'item_id': item.getElementsByClassName('data-product-sku')[0].textContent ?? '',
        //             'item_name': item.getElementsByTagName('a')[0].textContent ?? '',
        //             'coupon': item.getElementsByClassName('msc-cart-line-item-product-discount')[0]?.children[0]?.textContent?.split(': ')[1] ?? '',
        //             'discount': item.getElementsByClassName('msc-cart-line-item-product-discount')[0]?.children[1]?.textContent!.slice(0, -2) ?? '0',
        //             'item_discount_rate': item.getElementsByClassName('msc-cart-line-item-product-discount')[0]?.children[2]?.textContent?.substring(
        //                 (item.getElementsByClassName('msc-cart-line-item-product-discount')[0]?.children[2]?.textContent?.indexOf('(') ?? 0) + 1,
        //                 item.getElementsByClassName('msc-cart-line-item-product-discount')[0]?.children[2]?.textContent?.lastIndexOf(')')
        //             ) ?? '0%',
        //             'index': index,
        //             'item_category': localStorage.getItem(item.getElementsByClassName('data-product-sku')[0].textContent!)?.split(';')[0] ?? '',
        //             'item_list_name': localStorage.getItem(item.getElementsByClassName('data-product-sku')[0].textContent!)?.split(';')[1] ?? '',
        //             'price': item.getElementsByClassName('msc-price__actual')[0].textContent!.slice(0, -2) ?? '',
        //             'quantity': item.getElementsByClassName('quantity-value')[0].textContent ?? ''
        //         });
        //     });
        //     const item_object = { 'items': cart_items };
        //     return item_object;
        //     // });
        // }

        // // async function getDLCart() {
        // //     await sleep(3000);
        // //     for (let i = 0; i < window.dataLayer.length; i++) {
        // //         if (window.dataLayer[i].cart_items !== undefined) {
        // //             return window.dataLayer[i].cart_items;
        // //         }
        // //     }
        // // }

        // getCheckoutCartItemsGA();

        // temporary mirror cart from which to get data for the DL management
        // getDLCart().then(res => {
        //     const temp_cart = res;
        //     console.log(temp_cart);
        //     getCheckoutCart();
        // });
        // console.log(temp_cart);

        // function checkOutGA4() {
        //     console.log('started new checkout tracker GA4');
        //     // Select the node that will be observed for mutations
        //     const targetNode = document.querySelector('.ms-checkout__guided-form') as Node;
        
        //     // Options for the observer (which mutations to observe)
        //     const config = { attributes: true, childList: true, subtree: true };
        
        //     // Callback function to execute when mutations are observed
        //     const callback = (mutationsList: MutationRecord[]) => {
        //         // TODO this function was called twice before the page loaded completely in one of the tests,
        //         // be careful about sending wrong GA events
        //         // or maybe this is how to manage the pre filled ones
        //         // wait this might be called when the thingy is removed too oh no; it shouldn't be an issue tho
                
        //         // do stuff when loading disappears instead (as a removed node)?
        //         for (let mutation of mutationsList) {
        //             console.log(mutation.removedNodes);
        //             if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        //                 let pos = -1;
        //                 for (let i = 0; i < mutation.addedNodes.length; i++) {
        //                     // @ts-ignore
        //                     if (mutation.addedNodes[i].classList?.contains('ms-checkout__guided-card-footer')) {
        //                         pos = i;
        //                         break;
        //                     }
        //                 }
        
        //                 if (pos !== -1) {
        //                     console.log('right case');
        //                     // @ts-ignore
        //                     const new_button = mutation.addedNodes[pos].getElementsByClassName('ms-checkout__guided-card-btn-save')[0];
        //                     // @ts-ignore
        //                     const closestCard = mutation.target.closest('.ms-checkout__guided-card');
        //                     const cardHeader = closestCard.querySelector('.ms-checkout__guided-card-header');
        //                     const cardHeaderText = cardHeader.textContent.trim();
        //                     let variable: string;
        //                     switch (cardHeaderText[0]) {
        //                         case '1':
        //                             variable = 'robert 1';
        //                             break;
        //                         case '2':
        //                             variable = 'robert 2';
        //                             break;
        //                         case '3':
        //                             variable = 'robert 3';
        //                             break;
        //                         case '4':
        //                             variable = 'robert 4';
        //                             break;
        //                         case '5':
        //                             variable = 'robert 5';
        //                             break;
        //                         default:
        //                             variable = 'Unknown Value';
        //                     }
        //                     console.log(variable);
        //                     // change from click to remove the node so that it catches the auto changes too?
        //                     new_button.addEventListener('click', () => {
        //                         console.log('step ' + variable);
        //                     });
        //                 }
        //             }
        //         }
        //     };
        
        //     // Create an observer instance linked to the callback function
        //     const observer = new MutationObserver(callback);
        
        //     // Start observing the target node for configured mutations
        //     observer.observe(targetNode!, config);
        
        // }

        // checkOutGA4();

    // }, []);

    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <div className={'row'}>
                            <div className={'col-lg-7 '}>
                                <Node {...mainProps}>
                                    {guidedForm}
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    <Node {...mainControlProps}>
                                        {placeOrderButton}
                                        {keepShoppingButton}
                                    </Node>
                                </Node>
                            </div>
                            <div className={'col-lg-4 offset-lg-1'}>
                                <Node {...sideProps}>
                                    {orderSummary && <OrderSummaryComponent {...orderSummary} {...extraOrdersSummaryEu} />}
                                    <Node {...sideControlFirstProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        {placeOrderButton}
                                        {keepShoppingButton}
                                    </Node>
                                    {greeting.status ? (
                                        <div className={'ms-checkout__greeting'}>
                                            <h3>
                                                <span className={'icon-gift'} />
                                                {props.resources.greetingTitle}
                                            </h3>
                                            <div>
                                                <div>
                                                    <label>{props.resources.greetingMessage} </label>{' '}
                                                    <span>{greeting.greetingMessageCAP}</span>
                                                </div>
                                                <div>
                                                    <label>{props.resources.greetingSign} </label> <span>{greeting.greetingSignCAP}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={'ms-checkout__greeting'}>
                                            {/* tslint:disable-next-line:react-a11y-anchors */}
                                            <h4>
                                                <a href={'/cart'}>
                                                    <span className={'icon-gift'} /> {props.resources.addGreetingText}
                                                </a>
                                            </h4>
                                        </div>
                                    )}
                                    <div className={'ms-checkout__greeting'}>
                                        {/* tslint:disable-next-line:react-a11y-anchors */}
                                        <h4>
                                            <a href={'/cart'}>
                                                <span className={'icon-cart-btn'} /> {props.resources.insertModifyYourPromoCode}
                                            </a>
                                        </h4>
                                    </div>

                                    {lineItems && <LineItemsComponent {...lineItems} />}

                                    <Node {...sideControlSecondProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        {placeOrderButton}
                                        {keepShoppingButton}
                                    </Node>
                                </Node>
                            </div>
                        </div>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
